import { PauseCircle } from 'lucide-react';
import { Button } from '@sandtech-hub-monorepo/ui-kit';
import { Link } from 'react-router-dom';

import { ExternalApplicationLinks } from '../../../utils';
import { formatCalendarDate } from '../../dateFormat';

interface Props {
  /** "Leave of Absence" or "Deferral" — drives the heading and body copy. */
  kind: 'leaveOfAbsence' | 'deferral';
  /**
   * When the learner is expected back. Required on both instruments, so it is
   * normally present — optional here only so a paused enrollment with no open
   * row still renders the notice rather than disappearing from the page.
   */
  resumesOn?: string | null;
}

/**
 * Shown in place of the learner's coursework while their enrollment is paused.
 * Both states read the same way to the learner — no access, come back on a date,
 * talk to the registrar if that looks wrong — so one component covers both.
 */
export const EnrollmentPausedNotice = ({ kind, resumesOn }: Props) => {
  const isLeave = kind === 'leaveOfAbsence';

  const heading = isLeave
    ? 'You are currently on leave of absence'
    : 'Your enrollment is deferred';

  const body = isLeave
    ? 'You are not able to access your courses or academic activities while on leave.'
    : 'You have an active deferral and cannot access your program at this time.';

  const formattedResumesOn = resumesOn ? formatCalendarDate(resumesOn) : '';

  const resumeSentence = !formattedResumesOn
    ? ''
    : isLeave
    ? ` Your leave of absence is approved until ${formattedResumesOn}.`
    : ` Your enrollment will resume on ${formattedResumesOn}.`;

  return (
    <div className="border border-heading rounded-lg mb-6 overflow-hidden">
      <div className="flex items-start gap-6 p-6">
        <div className="shrink-0 rounded-full bg-primary/10 p-4 text-primary">
          <PauseCircle className="h-7 w-7" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-foreground">{heading}</h2>
          <p className="text-sm text-foreground/80">
            {body}
            {resumeSentence}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-heading px-6 py-4">
        <p className="text-sm text-foreground/80">
          If you believe this is a mistake or need assistance, please contact
          our support team.
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link
            to={ExternalApplicationLinks.support ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Registrar
          </Link>
        </Button>
      </div>
    </div>
  );
};
